// A cash movement on the shift (UC-67; brief T35): the screen's call to
// record cash out of or into the drawer of the cash point's open shift. Every
// rule is the store's (internal/core/domain/cash_movement.go and
// internal/core/usecase/uc67_cash_movement.go): the screen sends what was
// typed and shows what the store answers.

import { api, store } from '../api/client';
import type { components } from '../api/store';
import { message, type Language } from '../i18n/messages';
import { openDrawerNow, retryDrawer, watchDrawer } from './drawer';
import { attemptKey, loadShift } from './shift.svelte';

/** A movement's typed note, at most, in characters: MaxCashMovementNote in internal/core/domain/cash_movement.go. */
export const MAX_NOTE = 200;

type MovementRequest = components['schemas']['StoreCashMovementRequest'];
export type CashMovement = components['schemas']['StoreCashMovement'];
export type CashDirection = MovementRequest['direction'];
export type CashReason = MovementRequest['reason'];
export type Driver = components['schemas']['DriverBody'];

/**
 * The shop's active drivers for a driver float, in name order, as the till's
 * order store answers GET /api/till/drivers: from the server, or from the
 * list it kept while the server does not answer. Null when the store does not
 * answer either.
 */
export async function floatDrivers(): Promise<Driver[] | null> {
	try {
		const { data, error } = await api.GET('/api/till/drivers');
		if (error !== undefined || !data) return null;
		return data.drivers ?? [];
	} catch {
		return null;
	}
}

/**
 * The record of one T35 sheet: POST /store/shift/movements, keyed by what
 * was asked, so a try whose answer was lost is answered as before (INT-7)
 * and a changed request takes a new key. Null once the store recorded it,
 * else the words for why not.
 */
export function cashMovementAttempt() {
	const key = attemptKey();
	return async (language: Language, body: MovementRequest): Promise<string | null> => {
		try {
			const visit = seen;
			const before = await drawerKicks(body.direction);
			const { error } = await store.POST('/store/shift/movements', {
				body,
				params: {
					header: {
						'Idempotency-Key': key(
							`${body.direction}|${body.amountMinor}|${body.reason}|${body.note}|${body.driverId ?? ''}`,
						),
					},
				},
			});
			if (error === undefined) {
				await loadShift().catch(() => undefined);
				void watchKick(body.direction, before, visit);
				return null;
			}
			return refusalWords(language, error.code, error.details?.path);
		} catch {
			return message(language, 'offline');
		}
	};
}

/** The cashier words for a movement the store refused. */
export function refusalWords(language: Language, code: string, path: unknown): string {
	switch (code) {
		case 'NO_SHIFT':
			return message(language, 'cashMoveNoShift');
		case 'NOT_A_CASH_POINT':
			return message(language, 'shiftNotCashPoint');
		case 'NOT_FOUND':
			return message(language, 'cashMoveDriverNotFound');
		case 'DRIVER_INACTIVE':
			return message(language, 'cashMoveDriverRemoved');
		case 'VALIDATION_ERROR':
			// A note with no words, too many characters or a line break is
			// refused at note alike; its words ask for one line of why.
			if (path === 'note') return message(language, 'cashMoveNoteNeeded');
			if (path === 'amountMinor') return message(language, 'cashMoveAmountBounds');
			return message(language, 'ordersFailed');
		default:
			return message(language, 'ordersFailed');
	}
}

/**
 * How the last movement's drawer kick went, for Shift (T29) to say: watching
 * while the print queue sends it, failed or uncertain when it did not open
 * the drawer, idle otherwise. The kick is the one unprinted drawer job of the
 * movement's direction (cashDirection) that the queue lists after the movement
 * and did not list before it; none new means it printed already, or the shop
 * has no drawer, and an older movement's kick is never taken for it.
 */
export interface MovementDrawer {
	phase: 'idle' | 'watching' | 'failed' | 'uncertain';
	jobId: string;
}

let drawer = $state<MovementDrawer>({ phase: 'idle', jobId: '' });
let watching: AbortController | null = null;
// Counts each forget, so a movement recorded before a lock or a Back never
// starts its drawer's watch after it: the watch needs the visit it began in.
let seen = 0;

export function movementDrawer(): MovementDrawer {
	return drawer;
}

/** The unprinted drawer kicks of a direction the queue lists now; null when it does not answer. */
async function drawerKicks(direction: CashDirection): Promise<Set<string> | null> {
	try {
		const { data, error } = await store.GET('/store/print-jobs');
		if (error !== undefined || !data) return null;
		const kicks = data.jobs.filter(
			(job) => job.document === 'drawer' && job.cashDirection === direction,
		);
		return new Set(kicks.map((job) => job.id));
	} catch {
		return null;
	}
}

async function watch(jobId: string): Promise<void> {
	watching?.abort();
	const stop = new AbortController();
	watching = stop;
	drawer = { phase: 'watching', jobId };
	const result = await watchDrawer(jobId, stop.signal);
	if (stop.signal.aborted) return;
	drawer =
		result === 'failed'
			? { phase: 'failed', jobId }
			: typeof result === 'object'
				? { phase: 'uncertain', jobId }
				: { phase: 'idle', jobId: '' };
}

async function watchKick(
	direction: CashDirection,
	before: Set<string> | null,
	visit: number,
): Promise<void> {
	const after = await drawerKicks(direction);
	if (visit !== seen) return;
	const fresh = before && after ? [...after].filter((id) => !before.has(id)) : [];
	const jobId = fresh.length === 1 ? (fresh[0] ?? '') : '';
	if (jobId === '') {
		drawer = { phase: 'idle', jobId: '' };
		return;
	}
	await watch(jobId);
}

/**
 * Open drawer now on the movement's kick that did not open it: a retry of a
 * failed kick, or the decision on one held as uncertain (#515). The new kick
 * is watched as the first was.
 */
export async function openMovementDrawer(): Promise<void> {
	const { phase, jobId } = drawer;
	if (phase === 'failed') {
		const retry = await retryDrawer(jobId);
		if (retry) await watch(retry);
	} else if (phase === 'uncertain') {
		const outcome = await openDrawerNow(jobId);
		if (outcome.kind === 'opened') await watch(outcome.jobId);
	}
}

/** Forget the last movement's drawer, as Shift's Back or a lock does. */
export function forgetMovementDrawer(): void {
	seen++;
	watching?.abort();
	watching = null;
	drawer = { phase: 'idle', jobId: '' };
}
